import React, { useState } from "react";
import Seo from "@/components/Seo";
import { Breadcrumbs } from "@/components/layout/Breadcrumbs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { attribution, track } from "@/lib/analytics";
import { PHONE, PHONE_TEL } from "@/data/site";
import {
  Video, Headset, Wrench, Users, ShieldCheck, Phone, CheckCircle2,
  Volume2, AlertTriangle, BatteryCharging, HelpCircle, Globe, ArrowRight, PlayCircle,
} from "lucide-react";

const HERO_IMG = "https://customer-assets-rejwkqb3.emergentagent.net/job_seniorcar-discover/artifacts/8d72ca972830f198_108D837D-7807-4EB1-9526-FAC2148E18C2.png";
const CAR_CUTOUT = "https://static.prod-images.emergentagent.com/jobs/299cb05b-6ca7-4a7d-ab57-ec082781667b/images/6f71b81e390439fc1b5141875032c0790a2a95cca441b5f7109c5e1fc5b3abc4.jpeg";

const START_OPTIONS = [
  { id: "avvia", label: "Avvia assistenza" },
  { id: "videochiamata", label: "Prenota una videochiamata" },
  { id: "parla", label: "Parla con il Service Center" },
];
const PROBLEMS = [
  { id: "rumore", label: "Rumore strano", icon: Volume2 },
  { id: "spia", label: "Spia accesa", icon: AlertTriangle },
  { id: "ricarica", label: "Non si ricarica", icon: BatteryCharging },
  { id: "altro", label: "Altro", icon: HelpCircle },
];
const LEVELS = [
  { n: "01", icon: Video, title: "Video assistenza", text: "Parla direttamente con il nostro Service Center e mostra il problema attraverso il tuo smartphone. Il nostro tecnico potrà guidarti nei controlli in tempo reale.", note: "Tu mostri. Noi analizziamo." },
  { n: "02", icon: Wrench, title: "Remote fix", text: "Molti interventi possono essere risolti senza trasportare la SENIORCAR. Quando possibile, ti guideremo passo dopo passo durante il controllo o la sostituzione di un componente.", note: "Sempre con un tecnico SENIORCAR al tuo fianco." },
  { n: "03", icon: Users, title: "Il tuo tecnico + il nostro tecnico", text: "Hai un meccanico, un elettrauto o un tecnico di fiducia vicino a casa? Possiamo identificare il componente necessario, spedirlo dove serve e collegare il tuo tecnico con il nostro Service Center, assistendolo in video in tempo reale.", note: "Il tuo tecnico lavora sulla vettura. Il nostro lavora insieme a lui." },
  { n: "04", icon: Headset, title: "Se serve, ci pensiamo noi.", text: "Quando il problema non può essere risolto da remoto, il Service Center definisce insieme a te la soluzione più appropriata. Dalla diagnosi al ricambio fino all'intervento tecnico.", note: "Un unico interlocutore, dall'inizio alla fine." },
];
const LANGS = [
  { code: "it", label: "Italiano" }, { code: "en", label: "English" }, { code: "fr", label: "Français" },
  { code: "de", label: "Deutsch" }, { code: "es", label: "Español" }, { code: "nl", label: "Nederlands" },
];
const SHOW_EXAMPLES = [
  { n: "01", icon: Volume2, label: "Un rumore strano?" },
  { n: "02", icon: AlertTriangle, label: "Una spia accesa?" },
  { n: "03", icon: BatteryCharging, label: "La vettura non si ricarica?" },
  { n: "04", icon: HelpCircle, label: "Qualcosa non funziona come dovrebbe?" },
];

const scrollToForm = () => {
  const el = document.getElementById("assistenza-form");
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

const AssistanceForm = ({ start, setStart }) => {
  const [form, setForm] = useState({ first_name: "", last_name: "", phone: "", email: "", notes: "", privacy_consent: false, marketing_consent: false });
  const [problem, setProblem] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.phone) return toast.error("Nome e numero WhatsApp sono obbligatori.");
    if (!form.privacy_consent) return toast.error("È necessario accettare l'informativa privacy.");
    setLoading(true);
    try {
      const startLabel = START_OPTIONS.find((s) => s.id === start)?.label || "";
      const problemLabel = PROBLEMS.find((p) => p.id === problem)?.label || "-";
      const notes = [
        `Richiesta assistenza — Come iniziare: ${startLabel}`,
        `Tipo di problema: ${problemLabel}`,
        form.notes && `Descrizione: ${form.notes}`,
      ].filter(Boolean).join("\n");
      await api.post("/leads", {
        ...form, notes, lead_type: "assistenza", requested_action: start,
        product: "SENIORCAR", landing_page: "/assistenza", ...attribution(),
      });
      track("assistance_request", { start, problem });
      track("lead_form_submit", { form: "assistenza" });
      setSent(true);
    } catch { toast.error("Errore nell'invio. Riprova."); }
    finally { setLoading(false); }
  };

  if (sent) return (
    <div className="rounded-3xl bg-emerald-50 border border-emerald-200 p-10 text-center" data-testid="assistenza-success">
      <CheckCircle2 className="mx-auto text-emerald-700 mb-3" size={56} />
      <h3 className="text-2xl font-bold text-emerald-900" style={{ fontFamily: "Outfit" }}>Richiesta inviata!</h3>
      <p className="text-emerald-800/80 mt-2 text-lg">Grazie {form.first_name}. Un tecnico del Service Center ti contatterà su WhatsApp per la videochiamata.</p>
    </div>
  );

  return (
    <form onSubmit={submit} className="rounded-3xl bg-white border border-slate-200 shadow-md p-6 sm:p-8" data-testid="assistenza-form-el">
      <h3 className="text-xl font-extrabold text-slate-900 mb-5" style={{ fontFamily: "Outfit" }}>Richiesta di assistenza</h3>
      <div className="grid sm:grid-cols-2 gap-4">
        <div><label className="text-sm font-semibold text-slate-700">Nome *</label><Input value={form.first_name} onChange={set("first_name")} className="h-12 mt-1" data-testid="as-first-name" /></div>
        <div><label className="text-sm font-semibold text-slate-700">Cognome</label><Input value={form.last_name} onChange={set("last_name")} className="h-12 mt-1" data-testid="as-last-name" /></div>
        <div><label className="text-sm font-semibold text-slate-700">Numero WhatsApp *</label><Input value={form.phone} onChange={set("phone")} className="h-12 mt-1" data-testid="as-phone" /></div>
        <div><label className="text-sm font-semibold text-slate-700">Email</label><Input value={form.email} onChange={set("email")} className="h-12 mt-1" data-testid="as-email" /></div>
      </div>
      <div className="mt-4"><label className="text-sm font-semibold text-slate-700">Descrivi in breve il problema (e quando preferisci essere chiamato)</label>
        <Textarea value={form.notes} onChange={set("notes")} className="mt-1" rows={3} data-testid="as-notes" /></div>

      <p className="mt-6 text-sm font-semibold text-slate-700">Come vuoi iniziare?</p>
      <div className="mt-2 flex flex-wrap gap-2">
        {START_OPTIONS.map((o) => (
          <button key={o.id} type="button" onClick={() => setStart(o.id)}
            className={`px-4 py-2.5 rounded-full text-sm font-semibold border-2 transition-colors ${start === o.id ? "text-white border-transparent" : "text-slate-700 border-slate-200 hover:border-slate-300"}`}
            style={start === o.id ? { backgroundColor: "var(--sc-emerald)" } : {}} data-testid={`as-start-${o.id}`}>
            {o.label}
          </button>
        ))}
      </div>

      <p className="mt-6 text-sm font-semibold text-slate-700">Di cosa si tratta?</p>
      <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
        {PROBLEMS.map((p) => { const Ic = p.icon; return (
          <button key={p.id} type="button" onClick={() => setProblem(p.id)}
            className={`flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-semibold border-2 transition-colors ${problem === p.id ? "text-white border-transparent" : "text-slate-700 border-slate-200 hover:border-slate-300"}`}
            style={problem === p.id ? { backgroundColor: "var(--sc-emerald)" } : {}} data-testid={`as-problem-${p.id}`}>
            <Ic size={16} /> {p.label}
          </button>); })}
      </div>

      <label className="flex items-start gap-2 text-sm text-slate-600 mt-6">
        <Checkbox checked={form.privacy_consent} onCheckedChange={(v) => setForm({ ...form, privacy_consent: !!v })} className="mt-0.5" data-testid="as-privacy" />
        <span>Ho letto l'<a href="/privacy" className="underline">informativa privacy</a> e acconsento al trattamento dei dati per essere ricontattato. *</span>
      </label>
      <label className="flex items-start gap-2 text-sm text-slate-600 mt-3">
        <Checkbox checked={form.marketing_consent} onCheckedChange={(v) => setForm({ ...form, marketing_consent: !!v })} className="mt-0.5" data-testid="as-marketing" />
        <span>Desidero ricevere novità e offerte SENIORCAR (facoltativo).</span>
      </label>

      <Button type="submit" disabled={loading} className="w-full h-14 mt-6 text-lg font-bold text-white rounded-full" style={{ backgroundColor: "var(--sc-amber)" }} data-testid="as-submit">
        {loading ? "Invio in corso..." : "Invia la richiesta"}
      </Button>
      <p className="text-center text-xs text-slate-500 mt-3">Oppure chiama il Service Center: <a href={`tel:${PHONE_TEL}`} className="font-semibold text-emerald-800">{PHONE}</a></p>
    </form>
  );
};

export default function Assistenza() {
  const [start, setStart] = useState("avvia");
  const pick = (id) => { setStart(id); scrollToForm(); };

  return (
    <>
      <Seo title="SENIORCAR Service Center — Assistenza ovunque tu sia"
        description="Assistenza SENIORCAR in videochiamata: mostraci il problema, un tecnico fa la prima diagnosi e ti guida passo dopo passo. In tutta Europa, in più lingue."
        keywords="assistenza seniorcar, service center, assistenza videochiamata, assistenza mobilità senior" />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-6"><Breadcrumbs items={[{ label: "Assistenza" }]} /></div>

      {/* HERO */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-4 grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full text-white" style={{ backgroundColor: "var(--sc-emerald)" }}>
            <Headset size={14} /> SENIORCAR Service Center
          </span>
          <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 leading-[1.05]" style={{ fontFamily: "Outfit" }}>
            Sempre vicino a te.<br /><span style={{ color: "var(--sc-emerald)" }}>Ovunque tu sia.</span>
          </h1>
          <p className="mt-5 text-lg sm:text-xl text-slate-600 leading-relaxed">
            Abbiamo ripensato completamente il concetto di assistenza. Con SENIORCAR non devi cercare un'officina, trasportare la vettura per chilometri o aspettare giorni. Il nostro Service Center arriva direttamente da te.
          </p>
          <p className="mt-4 text-lg text-slate-600 leading-relaxed">
            Avvia una videochiamata e mostraci la tua SENIORCAR. Vediamo insieme il problema, facciamo una prima diagnosi e ti guidiamo passo dopo passo verso la soluzione.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3">
            <button onClick={() => pick("avvia")} className="inline-flex items-center justify-center gap-2 h-14 px-7 rounded-full font-extrabold text-lg text-white shadow-lg transition-transform hover:scale-[1.02]" style={{ backgroundColor: "var(--sc-amber)" }} data-testid="hero-avvia">
              <Video size={20} /> Avvia assistenza
            </button>
            <button onClick={() => pick("videochiamata")} className="inline-flex items-center justify-center gap-2 h-14 px-7 rounded-full font-bold text-lg border-2 border-slate-300 text-slate-800 hover:border-slate-400" data-testid="hero-videochiamata">
              Prenota una videochiamata
            </button>
          </div>
          <button onClick={() => pick("parla")} className="mt-3 inline-flex items-center gap-2 text-emerald-800 font-semibold hover:underline" data-testid="hero-parla">
            <Phone size={16} /> Parla con il Service Center
          </button>
        </div>
        <div className="relative">
          <img src={HERO_IMG} alt="Videochiamata di assistenza SENIORCAR con un tecnico del Service Center" loading="eager"
            className="w-full h-[360px] sm:h-[540px] object-contain object-center" data-testid="assistenza-hero-img" />
        </div>
      </section>

      {/* MOSTRACELO */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16 mt-6">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <img src={CAR_CUTOUT} alt="SENIORCAR" loading="lazy" className="w-full max-h-80 object-contain order-2 lg:order-1" />
          <div className="order-1 lg:order-2">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 leading-tight" style={{ fontFamily: "Outfit" }}>Mostracelo.<br />È più facile che spiegarlo.</h2>
            <p className="mt-4 text-lg text-slate-600">Non devi essere un tecnico e non devi spiegarci il problema. Mostracelo. Avvia una videochiamata con il Service Center e un nostro specialista potrà vedere direttamente la vettura e guidarti nei primi controlli.</p>
            <div className="mt-6 grid sm:grid-cols-2 gap-3">
              {SHOW_EXAMPLES.map((s) => { const Ic = s.icon; return (
                <div key={s.n} className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4" data-testid={`show-${s.n}`}>
                  <span className="text-sm font-extrabold" style={{ color: "var(--sc-emerald)" }}>{s.n}</span>
                  <Ic size={20} className="text-slate-500" />
                  <span className="font-semibold text-slate-800">{s.label}</span>
                </div>); })}
            </div>
          </div>
        </div>
      </section>

      {/* LIVELLI */}
      <section className="bg-slate-50 py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 leading-tight text-center" style={{ fontFamily: "Outfit" }}>Quattro livelli di assistenza.<br />Un solo interlocutore.</h2>
          <div className="mt-10 grid md:grid-cols-2 gap-5">
            {LEVELS.map((l) => { const Ic = l.icon; return (
              <div key={l.n} className="relative rounded-2xl bg-white border border-slate-200 p-6 shadow-sm" data-testid={`level-${l.n}`}>
                <div className="flex items-center gap-3">
                  <span className="w-11 h-11 rounded-xl flex items-center justify-center text-white shrink-0" style={{ backgroundColor: "var(--sc-emerald)" }}><Ic size={22} /></span>
                  <span className="text-2xl font-extrabold text-slate-200" style={{ fontFamily: "Outfit" }}>{l.n}</span>
                </div>
                <h3 className="mt-4 text-xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>{l.title}</h3>
                <p className="mt-2 text-slate-600 leading-relaxed">{l.text}</p>
                <p className="mt-4 text-sm font-semibold" style={{ color: "var(--sc-emerald)" }}>{l.note}</p>
              </div>); })}
          </div>
        </div>
      </section>

      {/* SENZA DISTANZE */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
        <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 text-center" style={{ fontFamily: "Outfit" }}>Assistenza senza distanze.</h2>
        <div className="mt-10 grid md:grid-cols-3 gap-5">
          <div className="rounded-2xl border border-slate-200 p-6 bg-white">
            <p className="text-sm font-bold uppercase tracking-wide text-slate-400">Tradizionalmente</p>
            <ol className="mt-3 space-y-2 text-slate-600">
              {["Cliente", "Officina", "Diagnosi", "Ricambio", "Nuovo appuntamento", "Riparazione"].map((s, i) => (
                <li key={i} className="flex items-center gap-2"><span className="text-xs font-bold text-slate-400 w-5">{i + 1}</span> {s}</li>
              ))}
            </ol>
          </div>
          <div className="rounded-2xl p-6 text-white shadow-lg" style={{ backgroundColor: "var(--sc-emerald)" }}>
            <p className="text-sm font-bold uppercase tracking-wide text-white/80">Noi l'abbiamo ripensata</p>
            <ol className="mt-3 space-y-2">
              {["Cliente", "SENIORCAR Service Center", "Diagnosi immediata", "Soluzione"].map((s, i) => (
                <li key={i} className="flex items-center gap-2 font-semibold"><span className="text-xs font-bold text-white/70 w-5">{i + 1}</span> {s}</li>
              ))}
            </ol>
          </div>
          <div className="rounded-2xl border border-slate-200 p-6 bg-white">
            <p className="text-sm font-bold uppercase tracking-wide text-slate-400">E quando serve un intervento</p>
            <ol className="mt-3 space-y-2 text-slate-600">
              {["Cliente", "Tecnico locale", "SENIORCAR Service Center"].map((s, i) => (
                <li key={i} className="flex items-center gap-2"><span className="text-xs font-bold text-slate-400 w-5">{i + 1}</span> {s}</li>
              ))}
            </ol>
            <p className="mt-4 text-sm font-semibold" style={{ color: "var(--sc-emerald)" }}>Tutti collegati insieme.</p>
          </div>
        </div>
      </section>

      {/* EUROPA / LINGUE */}
      <section className="bg-slate-900 py-16 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center">
          <Globe size={40} className="mx-auto mb-4" style={{ color: "var(--sc-emerald)" }} />
          <h2 className="text-3xl sm:text-4xl font-extrabold leading-tight" style={{ fontFamily: "Outfit" }}>Un Service Center.<br />Tante lingue. Tutta Europa.</h2>
          <p className="mt-4 text-lg text-white/70 max-w-2xl mx-auto">SENIORCAR nasce per essere europeo. Il nostro Service Center assiste clienti in diversi Paesi e nelle principali lingue europee. Non importa dove hai acquistato la tua SENIORCAR: il Service Center è sempre lo stesso.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            {LANGS.map((l) => (
              <span key={l.code} className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 px-4 py-2 font-semibold" data-testid={`lang-${l.code}`}>
                <span className="uppercase text-xs font-bold" style={{ color: "var(--sc-emerald)" }}>{l.code}</span> {l.label}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* FORM */}
      <section id="assistenza-form" className="max-w-5xl mx-auto px-4 sm:px-6 py-16 scroll-mt-24">
        <div className="text-center max-w-2xl mx-auto mb-8">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 leading-tight" style={{ fontFamily: "Outfit" }}>Non cercare un'officina.<br />Parla prima con noi.</h2>
          <p className="mt-4 text-lg text-slate-600">Lasciaci il tuo numero WhatsApp e raccontaci in breve cosa succede. Un tecnico del Service Center ti contatta su WhatsApp per la videochiamata.</p>
        </div>
        <AssistanceForm start={start} setStart={setStart} />
      </section>

      {/* FAQ link */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-20">
        <div className="rounded-3xl border border-slate-200 bg-white p-8 flex flex-col sm:flex-row items-center justify-between gap-4" data-testid="assistenza-faq">
          <div className="flex items-center gap-3">
            <ShieldCheck size={28} style={{ color: "var(--sc-emerald)" }} />
            <div>
              <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>Domande frequenti</h3>
              <p className="text-slate-600 text-sm">Garanzia, consegna, ricarica e assistenza: trovi tutte le risposte nelle FAQ.</p>
            </div>
          </div>
          <a href="/faq" className="inline-flex items-center gap-2 h-12 px-6 rounded-full font-bold text-white whitespace-nowrap" style={{ backgroundColor: "var(--sc-emerald)" }} data-testid="assistenza-faq-cta">
            Tutte le FAQ <ArrowRight size={18} />
          </a>
        </div>
      </section>
    </>
  );
}
